import type { TranslationKey } from '~/shared/internationalization/translation';

import type { AutomationLayer, AutomationMethod, AutomationPoint } from '../../data-contract/automation';

/**
 * Presentation model over the automation read API. Every ratio shown comes from the backend; the bands below
 * only bucket a share of effort into five readable steps for chips and fills, nothing here re-implements
 * the assessment.
 */
export const POTENTIAL_BANDS = ['NONE', 'LOW', 'MODERATE', 'HIGH', 'VERY_HIGH'] as const;
export type PotentialBand = (typeof POTENTIAL_BANDS)[number];

/** Upper edges of the first four bands; a share at or above the last edge is `VERY_HIGH`. */
const BAND_EDGES: readonly number[] = [0.075, 0.25, 0.475, 0.725];

export const potentialBandIndex = (band: PotentialBand): number => POTENTIAL_BANDS.indexOf(band);

const BAND_KEY: Record<PotentialBand, 'high' | 'low' | 'moderate' | 'none' | 'very-high'> = {
    HIGH: 'high',
    LOW: 'low',
    MODERATE: 'moderate',
    NONE: 'none',
    VERY_HIGH: 'very-high',
};

export const potentialBandTranslationKey = (band: PotentialBand | undefined): TranslationKey =>
    band === undefined
        ? 'pages.ai-transformation.band.no-data'
        : (`pages.ai-transformation.band.${BAND_KEY[band]}` satisfies TranslationKey);

/**
 * What happens to one task, in words: its score read as a hand-over. A reader scans a task list for
 * "who does this afterwards", not for a percentage of the whole role.
 */
export const handoverTranslationKey = (band: PotentialBand | undefined): TranslationKey =>
    band === undefined
        ? 'pages.ai-transformation.band.no-data'
        : (`pages.ai-transformation.handover.${BAND_KEY[band]}` satisfies TranslationKey);

export const layerTranslationKey = (layer: AutomationLayer): TranslationKey =>
    `pages.ai-transformation.layer.${layer}` satisfies TranslationKey;

/** The layer named for one item rather than many, for counts that read as a sentence. */
export const layerOneTranslationKey = (layer: AutomationLayer): TranslationKey =>
    `pages.ai-transformation.layer-one.${layer}` satisfies TranslationKey;

/** The layer whose items contain the given one; a family sits directly under the root. */
export const parentLayerOf = (layer: AutomationLayer): AutomationLayer | undefined => {
    switch (layer) {
        case 'family':
            return undefined;
        case 'cluster':
            return 'family';
        case 'role':
            return 'cluster';
        case 'level':
            return 'role';
    }
};

export const childLayerOf = (layer: AutomationLayer): AutomationLayer | undefined => {
    switch (layer) {
        case 'family':
            return 'cluster';
        case 'cluster':
            return 'role';
        case 'role':
            return 'level';
        case 'level':
            return undefined;
    }
};

/** The band a share of effort falls in: an item's exposure or a task's own score. */
export const bandOfExposure = (exposure: number | null | undefined): PotentialBand | undefined => {
    if (exposure === null || exposure === undefined) {
        return undefined;
    }

    const index = BAND_EDGES.findIndex((edge) => exposure < edge);

    return POTENTIAL_BANDS[index === -1 ? POTENTIAL_BANDS.length - 1 : index];
};

export const QUADRANTS = ['automation-opportunity', 'low-priority'] as const;
export type Quadrant = (typeof QUADRANTS)[number];

/** Which side of the method's high-exposure threshold an item sits on. */
export const quadrantOf = (exposure: number, method: AutomationMethod): Quadrant =>
    exposure >= method.exposureHigh ? 'automation-opportunity' : 'low-priority';

export const quadrantTranslationKey = (quadrant: Quadrant, part: 'subtitle' | 'title'): TranslationKey =>
    `pages.ai-transformation.quadrant.${quadrant}.${part}` satisfies TranslationKey;

export type AssessedPoint = AutomationPoint & {
    readonly coverage: number;
    readonly exposure: number;
};

export const isAssessed = (point: AutomationPoint): point is AssessedPoint =>
    typeof point.exposure === 'number' && typeof point.coverage === 'number';

export type PortfolioSummary = {
    readonly assessedCount: number;
    readonly coverage: number | undefined;
    readonly exposure: number | undefined;
    readonly itemCount: number;
};

export const summarizePortfolio = (points: readonly AutomationPoint[]): PortfolioSummary => {
    const assessed = points.filter(isAssessed);

    return {
        assessedCount: assessed.length,
        coverage:
            points.length > 0
                ? points.reduce((sum, point) => sum + (point.coverage ?? 0), 0) / points.length
                : undefined,
        exposure:
            assessed.length > 0
                ? assessed.reduce((sum, point) => sum + point.exposure, 0) / assessed.length
                : undefined,
        itemCount: points.length,
    };
};

export const formatHorizon = (horizon: string, locale: string): string => {
    const months = /^P(\d+)M$/.exec(horizon)?.[1];

    return months === undefined
        ? horizon
        : new Intl.NumberFormat(locale, { style: 'unit', unit: 'month', unitDisplay: 'long' }).format(Number(months));
};

/**
 * The share that stays with the person, as the whole-percent complement of the rounded exposure, so the two
 * printed numbers always add up to one hundred. Partitions from the backend arrive rounded; this is for the pair
 * the client derives itself.
 */
export const complementOf = (share: number | null | undefined): number | undefined =>
    share === null || share === undefined ? undefined : (100 - Math.round(share * 100)) / 100;

export const formatPercent = (value: number | null | undefined, locale: string): string =>
    value === null || value === undefined
        ? '–'
        : new Intl.NumberFormat(locale, { maximumFractionDigits: 0, style: 'percent' }).format(value);
